import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';


type ChildLocationMapProps = {
  latitude: string;
  longitude: string;
  onSelect: (latitude: number, longitude: number) => void;
};

function ChildLocationMap({
  latitude,
  longitude,
  onSelect,
}: ChildLocationMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const onSelectRef = useRef(onSelect);

  const token = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!token || !containerRef.current || mapRef.current) return;

    mapboxgl.accessToken = token;

    const parsedLat = Number(latitude);
    const parsedLng = Number(longitude);
    const hasCoordinates =
      latitude.trim() !== '' &&
      longitude.trim() !== '' &&
      Number.isFinite(parsedLat) &&
      Number.isFinite(parsedLng) &&
      parsedLat >= -90 &&
      parsedLat <= 90 &&
      parsedLng >= -180 &&
      parsedLng <= 180;

    // Default view: Laguna, Philippines.
    const center: [number, number] = hasCoordinates
      ? [parsedLng, parsedLat]
      : [121.4161, 14.2814];

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom: hasCoordinates ? 15 : 10,
    });

    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const setMarker = (lat: number, lng: number) => {
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        const marker = new mapboxgl.Marker({
          color: '#0d9488',
          draggable: true,
        })
          .setLngLat([lng, lat])
          .addTo(map);

        marker.on('dragend', () => {
          const position = marker.getLngLat();
          onSelectRef.current(position.lat, position.lng);
        });

        markerRef.current = marker;
      }

      onSelectRef.current(lat, lng);
    };

    map.on('load', () => {
      map.resize();
      if (hasCoordinates) setMarker(parsedLat, parsedLng);
    });

    map.on('click', (event) => {
      setMarker(event.lngLat.lat, event.lngLat.lng);
    });

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };

  }, [token]);

  if (!token) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Mapbox token is missing. Add <code>VITE_MAPBOX_TOKEN</code> to
        your frontend <code>.env</code> file and restart the Vite server.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        className="h-[350px] w-full overflow-hidden rounded-xl border border-gray-200"
        aria-label="Select the child's home location on the map"
      />
      <p className="text-xs text-gray-500">
        Click the map to place the marker, or drag the marker to adjust the
        location. The selected latitude and longitude will be used for this
        case's location coordinates.
      </p>
    </div>
  );
}

const steps = [
  { id: 1, label: 'Basic Info' },
  { id: 2, label: 'Child Details' },
  { id: 3, label: 'Guardian Info' },
  { id: 4, label: 'Incident Report' },
  { id: 5, label: 'Review' },
];

export default function CaseIntakePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [caseNumber, setCaseNumber] = useState<string | null>(null);
  const [evidenceImages, setEvidenceImages] = useState<File[]>([]);
  const [evidenceVideo, setEvidenceVideo] = useState<File | null>(null);
  const [incidentTypes, setIncidentTypes] = useState<any[]>([]);
  const [loadingIncidentTypes, setLoadingIncidentTypes] = useState(true);

  const [riskRules, setRiskRules] = useState({
    critical: 90,
    high: 80,
    moderate: 75,
  });

  const [loadingRiskRules, setLoadingRiskRules] = useState(true);

  const [form, setForm] = useState({
    // Basic Info
    reportedBy: '',
    reporterContact: '',
    reporterRelation: '',
    reportDate: new Date().toISOString().split('T')[0],
    reportSource: 'walk_in',
    // Child Details
    firstName: '',
    lastName: '',
    middleName: '',
    birthDate: '',
    age: '',
    gender: '',
    nationality: 'Filipino',
    religion: '',
    address: '',
    barangay: '',
    municipality: '',
    province: 'Batangas',
    schoolName: '',
    gradeLevel: '',
    // Guardian Info
    guardianName: '',
    guardianRelationship: '',
    guardianContact: '',
    guardianAddress: '',
    fatherName: '',
    motherName: '',
    siblings: '',
    profile_img: null as File | null,
    medical_conditions: '',
    medications: '',
    // Incident Report
    incidentType: '',
    incidentDate: '',
    incidentLocation: '',
    incidentDescription: '',
    incidentLatitude: '',
    incidentLongitude: '',
    perpetratorName: '',
    perpetratorRelation: '',
    witnessName: '',
    priorIncidents: 'no',
    // LGU Customizable
    lguCode: '',
    referralSource: '',
    priorityLevel: 'standard',

    nickname: '',
    placeOfBirth: '',
    civilStatus: '',
    section: '',
    childPosition: '',
    withDisability: '',
    soloParent: '',
    householdIdNo: '',
    philhealthNo: '',
    pwdIdNo: '',
    beneficiaryCategory: '',
    otherRelevantInfo: '',
    reasonForAssistance: '',
    contactNumber: '',
  });

  useEffect(() => {
    const aiData = localStorage.getItem('ai_case_data');

    if (aiData) {
      const parsed = JSON.parse(aiData);

      setForm((prev) => ({
        ...prev,

        firstName: parsed.firstName || '',
        lastName: parsed.lastName || '',
        middleName: parsed.middleName || '',

        age: parsed.age || '',
        gender: parsed.gender || '',

        birthDate: parsed.birthDate || '',

        religion: parsed.religion || '',

        address: parsed.address || '',
        barangay: parsed.barangay || '',
        municipality: parsed.municipality || '',
        province: parsed.province || '',

        schoolName: parsed.schoolName || '',
        gradeLevel: parsed.gradeLevel || '',

        guardianName: parsed.guardianName || '',
        guardianContact:
          parsed.guardianContact ||
          parsed.contactNumber ||
          '',

        fatherName: parsed.fatherName || '',
        motherName: parsed.motherName || '',

        siblings: parsed.siblings || '',

        incidentDescription: parsed.reason || '',

        incidentType: parsed.assistanceType || '',

        nickname: parsed.nickname || '',
        placeOfBirth: parsed.placeOfBirth || '',
        civilStatus: parsed.civilStatus || '',
        section: parsed.section || '',
        childPosition: parsed.childPosition || '',
        withDisability: parsed.withDisability || '',
        soloParent: parsed.soloParent || '',
        householdIdNo: parsed.householdIdNo || '',
        philhealthNo: parsed.philhealthNo || '',
        pwdIdNo: parsed.pwdIdNo || '',
        beneficiaryCategory: parsed.beneficiaryCategory || '',
        otherRelevantInfo: parsed.otherRelevantInfo || '',
        reasonForAssistance: parsed.reasonForAssistance || '',
      }));

      localStorage.removeItem('ai_case_data');
    }
  }, []);

  useEffect(() => {
    const loadIncidentTypes = async () => {
      try {
        setLoadingIncidentTypes(true);

        const response = await fetch(
          'https://guardiancloudcare.com/backend/api/get_system_config.php'
        );

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const result = await response.json();

        if (!result.success || !Array.isArray(result.data)) {
          throw new Error(
            result.error || 'Invalid system configuration response'
          );
        }

        const categoryConfig = result.data.find(
          (config: any) =>
            config.config_key === 'case_categories'
        );

        if (
          !categoryConfig ||
          !Array.isArray(categoryConfig.config_value)
        ) {
          setIncidentTypes([]);
          return;
        }

        const activeCategories = categoryConfig.config_value
          .filter((item: any) => {
            // Old format: "Physical Abuse"
            if (typeof item === 'string') {
              return true;
            }

            // Current Admin format:
            // inactive categories should not appear
            return item && item.active !== false;
          })
          .map((item: any, index: number) => {
            if (typeof item === 'string') {
              return {
                id: `category-${index}`,
                name: item,
                code: item
                  .toUpperCase()
                  .replace(/[^A-Z0-9]+/g, '_')
                  .replace(/^_+|_+$/g, ''),
                icon: 'ri-question-line',
              };
            }

            return {
              ...item,
              id:
                item.id ||
                `category-${index}`,
              name:
                item.name || '',
              code:
                item.code ||
                String(item.name || '')
                  .toUpperCase()
                  .replace(/[^A-Z0-9]+/g, '_')
                  .replace(/^_+|_+$/g, ''),
              icon:
                item.icon ||
                'ri-question-line',
            };
          })
          .filter(
            (item: any) => item.name
          );

        setIncidentTypes(activeCategories);

      } catch (error) {
        console.error(
          'Failed to load incident categories:',
          error
        );

        setIncidentTypes([]);
      } finally {
        setLoadingIncidentTypes(false);
      }
    };

    loadIncidentTypes();
  }, []);


  useEffect(() => {
    const loadRiskRules = async () => {
      try {
        setLoadingRiskRules(true);

        const response = await fetch(
          'https://guardiancloudcare.com/backend/api/get_system_config.php'
        );

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const result = await response.json();

        if (!result.success || !Array.isArray(result.data)) {
          throw new Error(
            result.error || 'Invalid system configuration response'
          );
        }

        const riskConfig = result.data.find(
          (config: any) => config.config_key === 'risk_rules'
        );

        if (!riskConfig) {
          console.warn('Risk rules configuration not found.');
          return;
        }

        const rules = riskConfig.config_value || {};

        const criticalRule =
          rules.critical &&
          typeof rules.critical === 'object'
            ? rules.critical
            : {};

        const highRule =
          rules.high &&
          typeof rules.high === 'object'
            ? rules.high
            : {};

        const moderateRule =
          rules.moderate &&
          typeof rules.moderate === 'object'
            ? rules.moderate
            : {};

        setRiskRules({
          critical: Number(criticalRule.minScore ?? 90),
          high: Number(highRule.minScore ?? 80),
          moderate: Number(moderateRule.minScore ?? 75),
        });
      } catch (error) {
        console.error('Failed to load risk rules:', error);
      } finally {
        setLoadingRiskRules(false);
      }
    };

    loadRiskRules();
  }, []);

  const update = (field: string, value: string | File | null) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleNext = () => {
    if (currentStep < steps.length) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const getRisk = (type: string) => {
    let score = 75; // base

    switch (type) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;

      case 'Physical Abuse':
        score = 90;
        break;

      case 'Emotional Abuse':
        score = 80;
        break;

      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;

      default:
        score = 70;
    }

    let level = 'low';

    if (score >= riskRules.critical) {
      level = 'critical';
    } else if (score >= riskRules.high) {
      level = 'high';
    } else if (score >= riskRules.moderate) {
      level = 'moderate';
    } else {
      level = 'low';
    }

    return {
      riskScore: score,
      riskLevel: level,
    };
  };

  const handleSubmit = async () => {
    try {
      const risk = getRisk(form.incidentType);

      const formData = new FormData();

      // Normal form fields
      Object.entries(form).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value as string | Blob);
        }
      });

      formData.append('uid', user?.uid || '');

      formData.append(
        'incident_latitude',
        form.incidentLatitude
      );

      formData.append(
        'incident_longitude',
        form.incidentLongitude
      );

      formData.append(
        'riskScore',
        String(risk.riskScore)
      );

      formData.append(
        'riskLevel',
        risk.riskLevel
      );

      evidenceImages.forEach((file) => {
        formData.append(
          'evidence_images[]',
          file
        );
      });


      if (evidenceVideo) {
        formData.append(
          'evidence_video',
          evidenceVideo
        );
      }

      const res = await fetch(
        'https://guardiancloudcare.com/backend/api/case_intake.php',
        {
          method: 'POST',
          body: formData,
        }
      );

      const text = await res.text();

      console.log(text);

      let data;

      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Invalid JSON:', text);
        alert('Server returned invalid response');
        return;
      }

      if (data.success) {
        setCaseNumber(data.case_number);
        setSubmitted(true);
      } else {
        alert('Failed: ' + data.error);
      }

    } catch (err) {
      console.error(err);
      alert('Server error');
    }
  };


  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
  const MAX_IMAGE_DIMENSION = 1920;

  const compressImage = (
    file: File,
    maxSize = MAX_IMAGE_SIZE
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        let width = img.width;
        let height = img.height;

        // Resize very large images
        if (
          width > MAX_IMAGE_DIMENSION ||
          height > MAX_IMAGE_DIMENSION
        ) {
          const ratio = Math.min(
            MAX_IMAGE_DIMENSION / width,
            MAX_IMAGE_DIMENSION / height
          );

          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not create image canvas.'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.85;

        const createCompressedFile = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Image compression failed.'));
                return;
              }

              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '') + '.jpg',
                {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                }
              );

              // If the file is still too large,
              // reduce JPEG quality.
              if (
                compressedFile.size > maxSize &&
                quality > 0.4
              ) {
                quality -= 0.1;
                createCompressedFile();
                return;
              }

              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };

        createCompressedFile();
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not read image.'));
      };

      img.src = objectUrl;
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };


  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];

    const processedImages: File[] = [];

    for (const file of files) {
      // File type validation
      if (!allowedTypes.includes(file.type)) {
        alert(
          `"${file.name}" is not a supported image format.\n\n` +
          `Allowed formats: JPG, PNG, WEBP`
        );
        continue;
      }

      try {
        let processedFile = file;

        // Automatically compress images that are large
        if (file.size > MAX_IMAGE_SIZE) {
          alert(
            `"${file.name}" is larger than 10 MB.\n\n` +
            `The system will automatically compress the image before uploading.`
          );

          processedFile = await compressImage(file);
        }

        // Final validation after compression
        if (processedFile.size > MAX_IMAGE_SIZE) {
          alert(
            `"${file.name}" is still larger than 10 MB after compression.\n\n` +
            `Please choose a smaller image.`
          );
          continue;
        }

        processedImages.push(processedFile);

      } catch (error) {
        console.error(
          'Image compression error:',
          error
        );

        alert(
          `Unable to process "${file.name}". Please try another image.`
        );
      }
    }

    if (processedImages.length > 0) {
      setEvidenceImages((prev) => [
        ...prev,
        ...processedImages,
      ]);
    }

    // Allow selecting the same file again
    e.target.value = '';
  };


  const handleVideoUpload = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      'video/mp4',
      'video/quicktime',
      'video/webm',
    ];

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      alert(
        'Please upload an MP4, MOV, or WEBM video.'
      );

      e.target.value = '';
      return;
    }

    // Validate file size
    const maxSize = 100 * 1024 * 1024;

    if (file.size > maxSize) {
      alert(
        'Video must not exceed 100MB.'
      );

      e.target.value = '';
      return;
    }

    // Validate video duration
    const video = document.createElement('video');

    video.preload = 'metadata';

    const objectUrl = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);

      const duration = video.duration;

      if (duration > 60) {
        alert(
          'Video must not exceed 1 minute (60 seconds).'
        );

        e.target.value = '';
        return;
      }

      setEvidenceVideo(file);

      e.target.value = '';
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      alert(
        'Unable to read the video. Please select a valid video file.'
      );

      e.target.value = '';
    };

    video.src = objectUrl;
  };


  const removeImage = (index: number) => {
    setEvidenceImages((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };


  const removeVideo = () => {
    setEvidenceVideo(null);
  };

  if (submitted) {
    return (
      <AppLayout title="Case Intake" breadcrumb={['Home', 'Case Intake']}>
        <div className="max-w-lg mx-auto mt-16 text-center">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-checkbox-circle-line text-teal-500 text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Case Successfully Filed!</h2>
          <p className="text-gray-500 mb-4">The case has been created and assigned a case number.</p>
          <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Case Number</p>
            <p className="text-xl font-mono font-bold text-teal-600">
              {caseNumber || 'Generating...'}
            </p>
            <p className="text-xs text-gray-400 mt-1">Assigned to: {user?.name}</p>
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/child-profiles')}
              className="bg-teal-500 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              View Child Profiles
            </button>
            <button
              onClick={() => { setSubmitted(false); setCurrentStep(1); }}
              className="bg-gray-100 text-gray-700 px-6 py-2.5 rounded-xl font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              New Case
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Case Intake Form" breadcrumb={['Home', 'Case Intake']}>
      <div className="max-w-3xl mx-auto">
        {/* Case Number Banner */}
        <div className="bg-teal-50 border border-teal-200 rounded-xl px-5 py-3 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <i className="ri-file-list-3-line text-teal-500" />
            <div>
              <p className="text-xs text-teal-600 font-medium">Auto-Generated Case Number</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-teal-600">
            <i className="ri-calendar-line" />
            <span>{form.reportDate}</span>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center mb-8 overflow-x-auto pb-2">
          {steps.map((step, idx) => (
            <div key={step.id} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
                    ${currentStep === step.id ? 'bg-teal-500 text-white' : currentStep > step.id ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-500'}`}
                >
                  {currentStep > step.id ? <i className="ri-check-line text-sm" /> : step.id}
                </div>
                <span className={`text-xs mt-1 whitespace-nowrap ${currentStep === step.id ? 'text-teal-600 font-semibold' : 'text-gray-400'}`}>
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`h-0.5 w-8 md:w-16 mx-1 mb-4 transition-all ${currentStep > step.id ? 'bg-teal-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-4">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Basic Information</h3>
              <p className="text-sm text-gray-500 mb-6">Who reported this case and when?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Reported By <span className="text-red-500">*</span></label>
                  <input value={form.reportedBy} onChange={(e) => update('reportedBy', e.target.value)} placeholder="Full name of reporter" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Contact Number <span className="text-red-500">*</span></label>
                  <input value={form.reporterContact} onChange={(e) => update('reporterContact', e.target.value)} placeholder="09XX-XXX-XXXX" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Relation to Child</label>
                  <select value={form.reporterRelation} onChange={(e) => update('reporterRelation', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="">Select relation</option>
                    <option>Parent</option>
                    <option>Relative</option>
                    <option>Neighbor</option>
                    <option>Teacher</option>
                    <option>Barangay Official</option>
                    <option>Health Worker</option>
                    <option>Anonymous</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Date of Report <span className="text-red-500">*</span></label>
                  <input type="date" value={form.reportDate} onChange={(e) => update('reportDate', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Report Source</label>
                  <select value={form.reportSource} onChange={(e) => update('reportSource', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="walk_in">Walk-in</option>
                    <option value="hotline">Hotline</option>
                    <option value="referral">Agency Referral</option>
                    <option value="barangay">Barangay Referral</option>
                    <option value="school">School Referral</option>
                    <option value="hospital">Hospital Referral</option>
                    <option value="online">Online Report</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Priority Level</label>
                  <select value={form.priorityLevel} onChange={(e) => update('priorityLevel', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="standard">Standard</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">LGU Code (Optional)</label>
                  <input value={form.lguCode} onChange={(e) => update('lguCode', e.target.value)} placeholder="e.g. BTC-2026" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Referral Source
                  </label>
                  <input value={form.referralSource} onChange={(e) => update('referralSource', e.target.value)} placeholder="e.g. DSWD, Police, NGO" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Assigned Social Worker</label>
                  <input value={user?.name || ''} readOnly className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2.5 text-sm text-gray-500" />
                </div>
              </div>
              {/* Evidence Upload */}
              <div className="mt-8 pt-6 border-t border-gray-100">

                <div className="mb-5">
                  <h4 className="text-base font-bold text-gray-900">
                    Evidence / Attachments
                  </h4>

                  <p className="text-sm text-gray-500 mt-1">
                    Upload photos and a video related to the case.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* IMAGES */}
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-teal-300 transition-colors">

                    <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-image-add-line text-2xl text-teal-500" />
                    </div>

                    <h5 className="font-semibold text-gray-800">
                      Case Images
                    </h5>

                    <p className="text-xs text-gray-400 mt-1 mb-4">
                      JPG, PNG, WEBP • Maximum 10MB each • Large images are automatically compressed
                    </p>

                    <label className="inline-flex items-center gap-2 bg-teal-500 text-white px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-teal-600 transition-colors">

                      <i className="ri-add-line" />

                      Add Images

                      <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        className="hidden"
                        onChange={handleImageUpload}
                      />

                    </label>

                    {evidenceImages.length > 0 && (
                      <p className="text-xs text-teal-600 mt-3 font-medium">
                        {evidenceImages.length} image
                        {evidenceImages.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>


                  {/* VIDEO */}
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-purple-300 transition-colors">

                    <div className="w-14 h-14 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-video-add-line text-2xl text-purple-500" />
                    </div>

                    <h5 className="font-semibold text-gray-800">
                      Case Video
                    </h5>

                    <p className="text-xs text-gray-400 mt-1 mb-4">
                      MP4, MOV, WEBM • Maximum 1 minute • Maximum 100MB
                    </p>

                    <label className="inline-flex items-center gap-2 bg-purple-500 text-white px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-purple-600 transition-colors">

                      <i className="ri-video-add-line" />

                      {evidenceVideo ? 'Change Video' : 'Add Video'}

                      <input
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm"
                        className="hidden"
                        onChange={handleVideoUpload}
                      />

                    </label>

                    {evidenceVideo && (
                      <p className="text-xs text-purple-600 mt-3 font-medium truncate">
                        {evidenceVideo.name}
                      </p>
                    )}
                  </div>

                </div>


                {/* IMAGE PREVIEWS */}
                {evidenceImages.length > 0 && (
                  <div className="mt-6">

                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-semibold text-gray-700">
                        Selected Images
                      </h5>

                      <span className="text-xs text-gray-400">
                        {evidenceImages.length} file(s)
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">

                      {evidenceImages.map((file, index) => {
                        const previewUrl = URL.createObjectURL(file);

                        return (
                          <div
                            key={`${file.name}-${index}`}
                            className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-square"
                          >

                            <img
                              src={previewUrl}
                              alt={`Evidence ${index + 1}`}
                              className="w-full h-full object-cover"
                            />

                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                                <p className="text-[10px] text-white truncate">
                                  {file.name}
                                </p>

                                <p className="text-[10px] text-teal-200">
                                  {formatFileSize(file.size)}
                                </p>
                              </div>

                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-500 transition-colors"
                            >
                              <i className="ri-close-line" />
                            </button>

                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                              <p className="text-[10px] text-white truncate">
                                {file.name}
                              </p>
                            </div>

                          </div>
                        );
                      })}

                    </div>
                  </div>
                )}


                {/* VIDEO PREVIEW */}
                {evidenceVideo && (
                  <div className="mt-6">

                    <h5 className="text-sm font-semibold text-gray-700 mb-3">
                      Selected Video
                    </h5>

                    <div className="relative bg-gray-900 rounded-xl overflow-hidden">

                      <video
                        controls
                        className="w-full max-h-80 object-contain"
                        src={URL.createObjectURL(evidenceVideo)}
                      />

                      <button
                        type="button"
                        onClick={removeVideo}
                        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500 transition-colors"
                      >
                        <i className="ri-close-line" />
                      </button>

                    </div>

                    <p className="text-xs text-gray-400 mt-2 truncate">
                      {evidenceVideo.name}
                    </p>

                  </div>
                )}


                {/* PRIVACY NOTICE */}
                <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">

                  <div className="flex gap-3">

                    <i className="ri-shield-check-line text-amber-600 text-lg" />

                    <p className="text-xs text-amber-700 leading-relaxed">
                      Upload only evidence relevant to this case.
                      Files should be handled securely and only accessed
                      by authorized personnel.
                    </p>

                  </div>

                </div>

              </div>
            </div>
          )}

          {/* Step 2: Child Details */}
          {currentStep === 2 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Child Details</h3>
              <p className="text-sm text-gray-500 mb-6">Personal information of the child involved.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">First Name <span className="text-red-500">*</span></label>
                  <input value={form.firstName} onChange={(e) => update('firstName', e.target.value)} placeholder="First name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Last Name <span className="text-red-500">*</span></label>
                  <input value={form.lastName} onChange={(e) => update('lastName', e.target.value)} placeholder="Last name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Middle Name</label>
                  <input value={form.middleName} onChange={(e) => update('middleName', e.target.value)} placeholder="Middle name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Nickname
                  </label>

                  <input
                    value={form.nickname}
                    onChange={(e) => update('nickname', e.target.value)}
                    placeholder="Nickname"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Date of Birth <span className="text-red-500">*</span></label>
                  <input type="date" value={form.birthDate} onChange={(e) => update('birthDate', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input  
                  type="number"  
                  value={form.age} 
                  onChange={(e) => update('age', e.target.value)} placeholder="Age"  min="0"  
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Place of Birth
                  </label>

                  <input
                    value={form.placeOfBirth}
                    onChange={(e) => update('placeOfBirth', e.target.value)}
                    placeholder="Place of birth"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Gender <span className="text-red-500">*</span></label>
                  <select value={form.gender} onChange={(e) => update('gender', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="">Select gender</option>
                    <option>Male</option>
                    <option>Female</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Civil Status
                  </label>

                  <input
                    value={form.civilStatus}
                    onChange={(e) => update('civilStatus', e.target.value)}
                    placeholder="Civil status"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Child Position in Family
                  </label>

                  <input
                    value={form.childPosition}
                    onChange={(e) => update('childPosition', e.target.value)}
                    placeholder="e.g. Middle Child"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    With Disability
                  </label>

                  <select
                    value={form.withDisability}
                    onChange={(e) => update('withDisability', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  >
                    <option value="">Select</option>
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Nationality <span className="text-red-500">*</span>
                  </label>
                  <input
                  value={form.nationality}  onChange={(e) => update('nationality', e.target.value)} placeholder="e.g. Filipino"className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"/>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Religion</label>
                  <input value={form.religion} onChange={(e) => update('religion', e.target.value)} placeholder="e.g. Roman Catholic" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Home Address <span className="text-red-500">*</span></label>
                  <input value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="House No., Street" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Barangay <span className="text-red-500">*</span></label>
                  <input value={form.barangay} onChange={(e) => update('barangay', e.target.value)} placeholder="Barangay" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Municipality/City <span className="text-red-500">*</span></label>
                  <input value={form.municipality} onChange={(e) => update('municipality', e.target.value)} placeholder="Municipality or City" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Province <span className="text-red-500">*</span> </label>
                  <input value={form.province} onChange={(e) => update('province', e.target.value)} placeholder="Province" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors"
                  />
                </div>
                <div className="md:col-span-2 mt-2">
                  <h4 className="text-base font-bold text-gray-900 mb-1">
                    Child Home Location
                  </h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Select the child's home location. These coordinates use the
                    existing incident latitude and longitude fields.
                  </p>
                  <ChildLocationMap
                    latitude={form.incidentLatitude}
                    longitude={form.incidentLongitude}
                    onSelect={(lat, lng) => {
                      setForm((prev) => ({
                        ...prev,
                        incidentLatitude: lat.toFixed(7),
                        incidentLongitude: lng.toFixed(7),
                      }));
                    }}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Latitude
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={form.incidentLatitude}
                        placeholder="Click the map to select"
                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Longitude
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={form.incidentLongitude}
                        placeholder="Click the map to select"
                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">School Name</label>
                  <input value={form.schoolName} onChange={(e) => update('schoolName', e.target.value)} placeholder="School name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Grade Level</label>
                  <select value={form.gradeLevel} onChange={(e) => update('gradeLevel', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="">Select grade</option>
                    <option>Kinder</option>
                    <option>Grade 1</option>
                    <option>Grade 2</option>
                    <option>Grade 3</option>
                    <option>Grade 4</option>
                    <option>Grade 5</option>
                    <option>Grade 6</option>
                    <option>Grade 7</option>
                    <option>Grade 8</option>
                    <option>Grade 9</option>
                    <option>Grade 10</option>
                    <option>Grade 11</option>
                    <option>Grade 12</option>
                    <option>Not enrolled</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Section
                  </label>

                  <input
                    value={form.section}
                    onChange={(e) => update('section', e.target.value)}
                    placeholder="Section"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Profile Image
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      update('profile_img', e.target.files?.[0] || null)
                    }
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Medical Conditions
                  </label>

                  <input
                    value={form.medical_conditions}
                    onChange={(e) => update('medical_conditions', e.target.value)}
                    placeholder="e.g. Asthma"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Medications
                  </label>

                  <input
                    value={form.medications}
                    onChange={(e) => update('medications', e.target.value)}
                    placeholder="e.g. Paracetamol"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Guardian Info */}
          {currentStep === 3 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Guardian / Family Information</h3>
              <p className="text-sm text-gray-500 mb-6">Details about the child&apos;s guardian and family.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Solo Parent
                  </label>

                  <select
                    value={form.soloParent}
                    onChange={(e) => {

                      const value = e.target.value;

                      setForm((prev) => ({
                        ...prev,
                        soloParent: value,

                        ...(value === 'Yes' && {
                          fatherName: '',
                          motherName: '',
                        }),
                      }));

                    }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  >
                    <option value="">Select</option>
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Guardian Name <span className="text-red-500">*</span></label>
                  <input value={form.guardianName} onChange={(e) => update('guardianName', e.target.value)} placeholder="Full name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Relationship to Child <span className="text-red-500">*</span></label>
                  <select value={form.guardianRelationship} onChange={(e) => update('guardianRelationship', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="">Select relationship</option>
                    <option>Mother</option>
                    <option>Father</option>
                    <option>Grandmother</option>
                    <option>Grandfather</option>
                    <option>Aunt</option>
                    <option>Uncle</option>
                    <option>Sibling</option>
                    <option>Foster Parent</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Contact Number <span className="text-red-500">*</span></label>
                  <input value={form.guardianContact} onChange={(e) => update('guardianContact', e.target.value)} placeholder="09XX-XXX-XXXX" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Number of Siblings</label>
                  <input type="number" value={form.siblings} onChange={(e) => update('siblings', e.target.value)} placeholder="0" min="0" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                {form.soloParent !== 'Yes' && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Father&apos;s Name</label>
                      <input value={form.fatherName} onChange={(e) => update('fatherName', e.target.value)} placeholder="Full name or Unknown/Absent" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Mother&apos;s Name</label>
                      <input value={form.motherName} onChange={(e) => update('motherName', e.target.value)} placeholder="Full name or Unknown/Absent" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                    </div>
                  </>
                )}
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Guardian&apos;s Address</label>
                  <input value={form.guardianAddress} onChange={(e) => update('guardianAddress', e.target.value)} placeholder="Complete address" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Household ID No.
                  </label>

                  <input
                    value={form.householdIdNo}
                    onChange={(e) => update('householdIdNo', e.target.value)}
                    placeholder="Household ID Number"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    PhilHealth No.
                  </label>

                  <input
                    value={form.philhealthNo}
                    onChange={(e) => update('philhealthNo', e.target.value)}
                    placeholder="PhilHealth Number"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    PWD ID No.
                  </label>

                  <input
                    value={form.pwdIdNo}
                    onChange={(e) => update('pwdIdNo', e.target.value)}
                    placeholder="PWD ID Number"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Beneficiary Category
                  </label>

                  <input
                    value={form.beneficiaryCategory}
                    onChange={(e) => update('beneficiaryCategory', e.target.value)}
                    placeholder="e.g. 4Ps DSWD Beneficiary"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Other Relevant Information
                  </label>

                  <textarea
                    value={form.otherRelevantInfo}
                    onChange={(e) => update('otherRelevantInfo', e.target.value)}
                    rows={3}
                    placeholder="Other relevant information"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Reason for Assistance
                  </label>

                  <textarea
                    value={form.reasonForAssistance}
                    onChange={(e) => update('reasonForAssistance', e.target.value)}
                    rows={3}
                    placeholder="Reason for assistance"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Incident Report */}
          {currentStep === 4 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Incident Report</h3>
              <p className="text-sm text-gray-500 mb-6">Details about the reported incident.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Type of Incident <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={form.incidentType}
                    onChange={(e) =>
                      update('incidentType', e.target.value)
                    }
                    disabled={loadingIncidentTypes}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">
                      {loadingIncidentTypes
                        ? 'Loading incident types...'
                        : 'Select type'}
                    </option>

                    {incidentTypes.map((category) => (
                      <option
                        key={category.id}
                        value={category.name}
                      >
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Date of Incident <span className="text-red-500">*</span></label>
                  <input type="date" value={form.incidentDate} onChange={(e) => update('incidentDate', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Location of Incident <span className="text-red-500">*</span></label>
                  <input value={form.incidentLocation} onChange={(e) => update('incidentLocation', e.target.value)} placeholder="Where did the incident occur?" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Incident Description <span className="text-red-500">*</span></label>
                  <textarea
                    value={form.incidentDescription}
                    onChange={(e) => update('incidentDescription', e.target.value)}
                    placeholder="Describe the incident in detail..."
                    rows={4}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{form.incidentDescription.length}/500</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Alleged Perpetrator Name</label>
                  <input value={form.perpetratorName} onChange={(e) => update('perpetratorName', e.target.value)} placeholder="Name or Unknown" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Perpetrator&apos;s Relation to Child</label>
                  <select value={form.perpetratorRelation} onChange={(e) => update('perpetratorRelation', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="">Select relation</option>
                    <option>Parent</option>
                    <option>Stepparent</option>
                    <option>Relative</option>
                    <option>Neighbor</option>
                    <option>Stranger</option>
                    <option>Teacher</option>
                    <option>Unknown</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Witness Name</label>
                  <input value={form.witnessName} onChange={(e) => update('witnessName', e.target.value)} placeholder="Name or None" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Prior Incidents?</label>
                  <select value={form.priorIncidents} onChange={(e) => update('priorIncidents', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 transition-colors">
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {currentStep === 5 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Review & Submit</h3>
              <p className="text-sm text-gray-500 mb-6">Please review all information before submitting.</p>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Case Information</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Case No.:</span> <span className="font-mono font-medium text-teal-600">{caseNumber}</span></div>
                    <div><span className="text-gray-500">Date:</span> <span className="font-medium">{form.reportDate}</span></div>
                    <div><span className="text-gray-500">Source:</span> <span className="font-medium capitalize">{form.reportSource.replace('_', ' ')}</span></div>
                    <div><span className="text-gray-500">Priority:</span> <span className="font-medium capitalize">{form.priorityLevel}</span></div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Child Information</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Name:</span> <span className="font-medium">{form.firstName} {form.middleName} {form.lastName}</span></div>
                    <div><span className="text-gray-500">Birth Date:</span> <span className="font-medium">{form.birthDate}</span></div>
                    <div><span className="text-gray-500">Gender:</span> <span className="font-medium">{form.gender}</span></div>
                    <div><span className="text-gray-500">Address:</span> <span className="font-medium">{form.barangay}, {form.municipality}</span></div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Incident Information</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Type:</span> <span className="font-medium">{form.incidentType}</span></div>
              
                    <div><span className="text-gray-500">Date:</span> <span className="font-medium">{form.incidentDate}</span></div>
                    <div className="col-span-2"><span className="text-gray-500">Description:</span> <span className="font-medium">{form.incidentDescription || 'Not provided'}</span></div>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Risk Level:</span>{' '}
                  <span className="font-medium capitalize">
                    {getRisk(form.incidentType).riskLevel}
                  </span>
                </div>

                <div>
                  <span className="text-gray-500">Risk Score:</span>{' '}
                  <span className="font-medium">
                    {getRisk(form.incidentType).riskScore}%
                  </span>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">

                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Evidence / Attachments
                  </p>

                  <div className="space-y-2">

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Images
                      </span>

                      <span className="text-sm font-semibold text-gray-800">
                        {evidenceImages.length}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Video
                      </span>

                      <span className="text-sm font-semibold text-gray-800">
                        {evidenceVideo ? '1 attached' : 'None'}
                      </span>
                    </div>

                  </div>

                </div>
                <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <p className="text-xs text-teal-700">
                    <i className="ri-information-line mr-1" />
                    By submitting, you certify that all information provided is accurate and complete. This case will be assigned to you and submitted for supervisor review.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Bar */}
        <div className="bg-white rounded-xl border border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
          </div>
          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrev}
                className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Previous
              </button>
            )}
            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                className="text-sm bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                Next
                <i className="ri-arrow-right-line" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="text-sm bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-send-plane-line" />
                Submit Case
              </button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
